import React from 'react';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/themes/material_blue.css';
import { CITIZENSHIP_STATUS, CITIZENSHIP_LABELS } from './constants';
import Tooltip from './Tooltip';

const getGenerationLabel = (gen) => {
  if (gen === 0) return 'Applicant';
  if (gen === 1) return 'Parent';
  if (gen === 2) return 'Grandparent';
  return `${'Great-'.repeat(gen - 2)}Grandparent`;
};

const getStatusLabel = (status) => {
  if (status === 'NOT_SURE') return 'Not Sure';
  return CITIZENSHIP_LABELS[status] || status;
};

const Person = ({ person, onPersonChange, onAddAncestor, onRemoveAncestor, onCalculate, child, generation = 0, showDocs, isMobile, useExactDates }) => {
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const updatedPerson = { ...person, [name]: value };
    if (name === 'isCitizen') {
      updatedPerson.calculatedStatus = null;
      if (value === CITIZENSHIP_STATUS.BORN_IN_CANADA || value === CITIZENSHIP_STATUS.BY_DESCENT) {
        updatedPerson.citizenshipDate = person.birthYear;
      } else if (value === CITIZENSHIP_STATUS.NATURALIZED) {
        if (!useExactDates) {
          if (person.birthYear) {
            const defaultNatDate = new Date(person.birthYear);
            defaultNatDate.setFullYear(defaultNatDate.getFullYear() + 10);
            updatedPerson.citizenshipDate = defaultNatDate;
          } else {
            updatedPerson.citizenshipDate = null;
          }
        } else {
          updatedPerson.citizenshipDate = null;
        }
      } else if (value === CITIZENSHIP_STATUS.NO || value === 'NOT_SURE') {
        updatedPerson.citizenshipDate = null;
      }
    }
    onPersonChange(person.id, updatedPerson);
  };

  const handleDateChange = ([date]) => {
    if (!date) return;

    const updatedPerson = { ...person, birthYear: date };
    const isCalculated = person.ancestors && person.ancestors.length > 0 && person.calculatedStatus !== null;
    const effectiveStatus = isCalculated ? person.calculatedStatus : person.isCitizen;

    // If the person is a citizen by birth, their citizenship date is their birth date.
    // This should apply even if the status is calculated.
    // EXCEPTION: If user manually selected Naturalized, keep their manual date (don't overwrite with birth year).
    if ([CITIZENSHIP_STATUS.BORN_IN_CANADA, CITIZENSHIP_STATUS.BY_DESCENT].includes(effectiveStatus) && person.isCitizen !== CITIZENSHIP_STATUS.NATURALIZED) {
      updatedPerson.citizenshipDate = date;
    }

    // If using exact dates, clear the manual overrides so date math takes over
    onPersonChange(person.id, {
      ...updatedPerson,
      naturalizedBeforeBirth: null,
      wasUnder21: null
    });
  };

  const handleDocChange = (docType, isChecked) => {
    const currentDocs = person.documentation || {};
    const updatedDocs = { ...currentDocs, [docType]: isChecked };
    onPersonChange(person.id, { ...person, documentation: updatedDocs });
  };

  const handleCitizenshipDateChange = ([date]) => {
    onPersonChange(person.id, {
      ...person,
      citizenshipDate: date,
      naturalizedBeforeBirth: null,
      wasUnder21: null
    });
  };

  // Helper to handle the simplified dropdown changes
  const handleSimplifiedDateChange = (type, value) => {
    const updates = { ...person };

    if (type === 'birth') {
      if (value === 'POST_2025') updates.birthYear = new Date(2025, 11, 15);
      else updates.birthYear = new Date(1990, 0, 1);
    }
    else if (type === 'naturalization') {
      // Reset special flags when changing bucket
      // updates.naturalizedInNewfoundland = false; // Don't reset this, it's now a separate toggle
      const isNfld = person.naturalizedInNewfoundland;

      if (value === 'POST_1947') {
        updates.citizenshipDate = new Date(1980, 0, 1);
      } else if (value === 'PRE_1947') {
        updates.citizenshipDate = isNfld
          ? new Date(1949, 2, 31) // Pre-1949 for NFLD
          : new Date(1946, 11, 31); // Pre-1947 for Standard
      }
    }
    onPersonChange(person.id, updates);
  };

  // Determine current bucket for dropdown value
  const getBirthBucket = () => {
    if (!person.birthYear) return 'PRE_2025';
    const d = new Date(person.birthYear);
    if (d >= new Date(2025, 11, 15)) return 'POST_2025';
    return 'PRE_2025';
  };

  const getNatBucket = () => {
    if (!person.citizenshipDate) return 'POST_1947';
    const d = new Date(person.citizenshipDate);
    const cutoff = person.naturalizedInNewfoundland ? new Date(1949, 3, 1) : new Date(1947, 0, 1);
    if (d < cutoff) return 'PRE_1947';
    return 'POST_1947';
  };

  const isCalculated = person.ancestors && person.ancestors.length > 0 && person.calculatedStatus !== null;
  const effectiveStatus = isCalculated ? person.calculatedStatus : person.isCitizen;

  const isNaturalizedButEligible = person.isCitizen === CITIZENSHIP_STATUS.NATURALIZED && person.calculatedStatus === CITIZENSHIP_STATUS.BY_DESCENT;

  // Check for the Pre-1947 Naturalization Exception
  const isPre1947Exception = (() => {
    if (!person.ancestors || person.ancestors.length === 0) return false;
    const parent = person.ancestors[0];

    // If parent has manual flag set to FALSE (Naturalized AFTER birth), then this exception applies
    // (Exception = Born BEFORE Nat, so Nat AFTER Birth means Exception applies? No.)
    // Exception applies if Born BEFORE Nat.
    // naturalizedBeforeBirth = TRUE means Nat BEFORE Birth.
    // naturalizedBeforeBirth = FALSE means Nat AFTER Birth (Born BEFORE Nat).
    if (parent.naturalizedBeforeBirth === false) return true;

    const parentStatus = parent.calculatedStatus ?? parent.isCitizen;
    if (parentStatus !== CITIZENSHIP_STATUS.NATURALIZED || !parent.citizenshipDate) return false;
    const birthDate = new Date(person.birthYear);
    const natDate = new Date(parent.citizenshipDate);
    const cutoffDate = parent.naturalizedInNewfoundland ? new Date(1949, 3, 1) : new Date(1947, 0, 1);
    // Exception applies if naturalized BEFORE or ON the day legislation came into effect (Transitional/Deemed) AND child was born BEFORE naturalization
    if (birthDate <= natDate && natDate <= cutoffDate) {
      return true;
    }
    return false;
  })();

  const getBackgroundColor = () => {
    if ([
      CITIZENSHIP_STATUS.BORN_IN_CANADA,
      CITIZENSHIP_STATUS.BY_DESCENT,
      CITIZENSHIP_STATUS.NATURALIZED
    ].includes(effectiveStatus)) return '#ecfdf5';
    if (effectiveStatus === 'NOT_SURE') return '#fefce8'; // Yellow background
    // Default/Pending color (Blue-ish) until calculated or manually set to Yes
    if (person.calculatedStatus === null && person.isCitizen === CITIZENSHIP_STATUS.NO) return '#f0f9ff';
    if (effectiveStatus === CITIZENSHIP_STATUS.NO) return '#fef2f2';
    return '#ffffff';
  };

  const getBorderColor = () => {
    if ([
      CITIZENSHIP_STATUS.BORN_IN_CANADA,
      CITIZENSHIP_STATUS.BY_DESCENT,
      CITIZENSHIP_STATUS.NATURALIZED
    ].includes(effectiveStatus)) return '#6ee7b7';
    if (effectiveStatus === 'NOT_SURE') return '#fde047'; // Yellow border
    // Default/Pending border
    if (person.calculatedStatus === null && person.isCitizen === CITIZENSHIP_STATUS.NO) return '#bae6fd';
    if (effectiveStatus === CITIZENSHIP_STATUS.NO) return '#fca5a5';
    return '#e5e7eb';
  };

  // Only show the 3-year proof checkbox if the parent was born OUTSIDE Canada.
  // Parents born IN Canada or Naturalized transmit citizenship automatically to the first generation.
  const showCheckbox = child && new Date(child.birthYear) >= new Date(2025, 11, 15) &&
    effectiveStatus !== CITIZENSHIP_STATUS.BORN_IN_CANADA &&
    effectiveStatus !== CITIZENSHIP_STATUS.NATURALIZED;

  const getMaxDate = () => {
    if (!child || !child.birthYear) return undefined;
    const date = new Date(child.birthYear);
    date.setDate(date.getDate() - 1);
    return date;
  };

  const getSourceGeneration = () => {
    if (![
      CITIZENSHIP_STATUS.BORN_IN_CANADA,
      CITIZENSHIP_STATUS.BY_DESCENT,
      CITIZENSHIP_STATUS.NATURALIZED
    ].includes(effectiveStatus) || !person.ancestors || person.ancestors.length === 0) return null;

    const limitDate = new Date(2025, 11, 15);
    const birthDate = new Date(person.birthYear);

    const sourceAncestor = person.ancestors.find((ancestor) => {
      const ancestorEffectiveStatus = ancestor.calculatedStatus ?? ancestor.isCitizen;
      if (![
        CITIZENSHIP_STATUS.BORN_IN_CANADA,
        CITIZENSHIP_STATUS.BY_DESCENT,
        CITIZENSHIP_STATUS.NATURALIZED
      ].includes(ancestorEffectiveStatus)) return false;

      if (ancestor.citizenshipDate) {
        if (birthDate <= new Date(ancestor.citizenshipDate)) return false;
      }

      if (birthDate < limitDate) return true;
      return ancestor.threeYearsCumulative === true;
    });

    if (sourceAncestor) {
      return getGenerationLabel(generation + 1);
    }
    return null;
  };

  const sourceGen = getSourceGeneration();

  const isParentYounger = child && new Date(person.birthYear) > new Date(child.birthYear);

  const parentMissingNatDate = person.ancestors && person.ancestors.some(a => a.isCitizen === CITIZENSHIP_STATUS.NATURALIZED && !a.citizenshipDate);

  const getIneligibilityReason = () => {
    if (effectiveStatus !== CITIZENSHIP_STATUS.NO || !person.ancestors || person.ancestors.length === 0) return null;

    const parent = person.ancestors[0];
    const parentStatus = parent.calculatedStatus ?? parent.isCitizen;
    const birthDate = person.birthYear ? new Date(person.birthYear) : null;
    const parentBirthDate = parent.birthYear ? new Date(parent.birthYear) : null;

    if (birthDate && parentBirthDate && parentBirthDate > birthDate) {
      return `Data error: The ${getGenerationLabel(generation + 1)} cannot be born after the ${getGenerationLabel(generation)}.`;
    }

    if (parentStatus === CITIZENSHIP_STATUS.NO) {
      return `The ${getGenerationLabel(generation + 1)} is not a Canadian citizen.`;
    }

    if (parentStatus === 'NOT_SURE') {
      return `The citizenship status of the ${getGenerationLabel(generation + 1)} is uncertain. Please provide more information.`;
    }

    if (parentStatus === CITIZENSHIP_STATUS.NATURALIZED) {
      if (!parent.citizenshipDate) {
        return `Citizenship status is unclear because the ${getGenerationLabel(generation + 1)}'s naturalization date is missing.`;
      }

      const bornBeforeNaturalization = parent.naturalizedBeforeBirth === false || (birthDate && new Date(parent.citizenshipDate) >= birthDate);
      if (bornBeforeNaturalization) {
        const natDate = new Date(parent.citizenshipDate);
        const cutoffDate = parent.naturalizedInNewfoundland ? new Date(1949, 3, 1) : new Date(1947, 0, 1);
        if (natDate > cutoffDate) {
          return `Citizenship not passed down: The ${getGenerationLabel(generation)} was born before the ${getGenerationLabel(generation + 1)} naturalized (Post-1947/49 Rule).`;
        } else {
          // Pre-1947/49 rule applies, check age exception
          let age = -1;
          if (birthDate) {
            age = natDate.getFullYear() - birthDate.getFullYear();
            const m = natDate.getMonth() - birthDate.getMonth();
            if (m < 0 || (m === 0 && natDate.getDate() < birthDate.getDate())) {
              age--;
            }
          }

          const isUnder21 = parent.wasUnder21 ?? (age !== -1 && age < 21);

          if (!isUnder21) {
            return `Citizenship not passed down: The ${getGenerationLabel(generation)} was 21 or older when the parent naturalized (Pre-1947/49 Exception Rule).`;
          }
        }
      }
    }

    if (parentStatus === CITIZENSHIP_STATUS.BY_DESCENT) {
      const limitDate = new Date(2025, 11, 15);
      if (birthDate && birthDate >= limitDate) {
        if (!parent.threeYearsCumulative && !parent.crownServant) {
          return `Citizenship not passed down: The ${getGenerationLabel(generation + 1)} (born outside Canada) did not meet the 'Substantial Connection' test (3 years residency or Crown service) for a child born after Dec 15, 2025.`;
        }
      }
    }
    return `Citizenship could not be passed down from the ${getGenerationLabel(generation + 1)}. Please check all entered information.`;
  };

  const hasDeepAncestry = (p, currentGen) => {
    if (currentGen >= 3) return true;
    if (p.ancestors && p.ancestors.length > 0) {
      return p.ancestors.some(a => hasDeepAncestry(a, currentGen + 1));
    }
    return false;
  };
  const isDeepAncestry = generation === 0 && hasDeepAncestry(person, 0);

  // Logic for Child Card to show Parent questions
  const parent = person.ancestors && person.ancestors.length > 0 ? person.ancestors[0] : null;
  const parentIsNaturalized = parent && parent.isCitizen === CITIZENSHIP_STATUS.NATURALIZED;

  const getParentNatBucket = () => {
    if (!parent || !parent.citizenshipDate) return 'POST_1947';
    const d = new Date(parent.citizenshipDate);
    const cutoff = parent.naturalizedInNewfoundland ? new Date(1949, 3, 1) : new Date(1947, 0, 1);
    if (d < cutoff) return 'PRE_1947';
    return 'POST_1947';
  };

  const handleParentUpdate = (field, value) => {
    if (!parent) return;
    const updatedParent = { ...parent, [field]: value };
    // If toggling "Born Before", we might need to reset "Under 21" if it becomes irrelevant, 
    // but keeping it simple is fine as logic handles relevance.
    if (field === 'naturalizedBeforeBirth') {
      if (value === true) {
        // If switching to "Born After" (naturalizedBeforeBirth = true), Under 21 is irrelevant.
        updatedParent.wasUnder21 = null;
      } else if (updatedParent.wasUnder21 === null || updatedParent.wasUnder21 === undefined) {
        updatedParent.wasUnder21 = false;
      }
    }
    onPersonChange(parent.id, updatedParent);
  };

  // Styles
  const cardStyle = {
    border: `1px solid ${getBorderColor()}`,
    borderRadius: '12px',
    padding: '16px',
    margin: '0',
    width: '600px',
    maxWidth: '95%',
    backgroundColor: getBackgroundColor(),
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    transition: 'all 0.2s ease-in-out',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '6px',
    fontSize: '0.875rem',
    fontWeight: '600',
    color: '#374151',
  };

  const inputGroupStyle = {
    marginBottom: '12px',
  };

  const selectStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '1rem',
    boxSizing: 'border-box', // Ensure padding doesn't add to width
    backgroundColor: (isCalculated && !isNaturalizedButEligible) ? '#f3f4f6' : 'white',
    color: (isCalculated && !isNaturalizedButEligible) ? '#6b7280' : '#111827',
    cursor: (isCalculated && !isNaturalizedButEligible) ? 'not-allowed' : 'pointer',
  };

  const dateInputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: '6px',
    border: '1px solid #d1d5db',
    fontSize: '1rem',
    boxSizing: 'border-box',
  };

  const buttonBaseStyle = {
    padding: '10px 16px',
    borderRadius: '6px',
    border: 'none',
    fontSize: '0.875rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const docsStyle = {
    border: '1px solid #e5e7eb',
    borderRadius: '12px',
    padding: '16px',
    width: isMobile ? '100%' : '300px',
    maxWidth: isMobile ? '95%' : undefined,
    backgroundColor: '#ffffff',
    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
    fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
    height: 'fit-content',
    display: 'flex',
    flexDirection: 'column',
  };

  const containerStyle = {
    display: 'flex',
    flexDirection: isMobile ? 'column' : 'row',
    justifyContent: 'flex-start',
    alignItems: isMobile ? 'center' : 'flex-start',
    gap: '20px',
    fontSize: '1rem',
    backgroundColor: 'white',
    color: '#111827',
    fontFamily: 'inherit',
    textAlign: 'left',
  };

  const treeLineStyle = {
    marginLeft: isMobile ? 0 : '40px',
    paddingLeft: isMobile ? 0 : '20px',
    borderLeft: isMobile ? 'none' : '2px solid #e5e7eb',
    marginTop: '20px',
  };

  return (
    <div style={{ margin: (generation === 0 || isMobile) ? '0 auto' : undefined, width: isMobile ? '100%' : 'fit-content' }}>
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', borderBottom: '1px solid rgba(0,0,0,0.05)', paddingBottom: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              {[
                CITIZENSHIP_STATUS.BORN_IN_CANADA,
                CITIZENSHIP_STATUS.BY_DESCENT,
                CITIZENSHIP_STATUS.NATURALIZED
              ].includes(effectiveStatus) && (
                  <img src="/canadian_flag.png" alt="Canadian Flag" style={{ width: '40px' }} />
                )}
              <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#111827' }}>{getGenerationLabel(generation)}</h3>
              {generation > 0 && (
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155', marginLeft: isMobile ? 0 : '10px', whiteSpace: 'nowrap' }}>
                  <input
                    type="checkbox"
                    checked={person.alsoApplying || false}
                    onChange={(e) =>
                      onPersonChange(person.id, {
                        ...person,
                        alsoApplying: e.target.checked,
                      })
                    }
                    style={{ width: '16px', height: '16px', marginRight: '6px', accentColor: '#2563eb' }}
                  />
                  Also Applying?
                </label>
              )}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#9ca3af', backgroundColor: 'rgba(255,255,255,0.5)', padding: '2px 6px', borderRadius: '4px' }}></span>
          </div>

          {showCheckbox && (
            <>
              <div style={{ marginBottom: '8px', padding: '10px 12px', backgroundColor: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '4px', fontSize: '0.82rem', color: '#92400e' }}>
                <strong>Why is this here?</strong> This parent was born outside Canada and has a child born on or after December 15, 2025. Bill C-3 introduced a new residency requirement for this situation.
              </div>
              <div style={{ ...inputGroupStyle, backgroundColor: 'rgba(255,255,255,0.6)', padding: '12px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155' }}>
                  <input
                    type="checkbox"
                    checked={person.threeYearsCumulative}
                    onChange={(e) =>
                      onPersonChange(person.id, {
                        ...person,
                        threeYearsCumulative: e.target.checked,
                      })
                    }
                    style={{ width: '16px', height: '16px', marginRight: '12px', accentColor: '#2563eb' }}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Lived 1,095 cumulative days in Canada (3 Years)
                    <Tooltip definition="Required by Bill C-3 for parents born outside Canada whose child is born after December 15, 2025. Days do not need to be consecutive — they can be spread across multiple visits or periods of residence." />
                  </span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#334155', marginTop: '8px' }}>
                  <input
                    type="checkbox"
                    checked={person.crownServant || false}
                    onChange={(e) =>
                      onPersonChange(person.id, {
                        ...person,
                        crownServant: e.target.checked,
                      })
                    }
                    style={{ width: '16px', height: '16px', marginRight: '12px', accentColor: '#2563eb' }}
                  />
                  <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    Employed as Crown Servant at time of child's birth?
                    <Tooltip definition="Includes members of the Canadian Armed Forces, the federal public administration, and provincial/territorial public service employees who are posted or employed outside Canada." />
                  </span>
                </label>
              </div>
            </>
          )}

          {/* Questions about Parent's Naturalization (Moved to Child Card) */}
          {parentIsNaturalized && !useExactDates && parent.citizenshipDate && (
            <>
              <div style={{ marginBottom: '16px', padding: '12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#374151', marginBottom: '8px' }}>
                  Relationship to Parent's Naturalization
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                    <input
                      type="checkbox"
                      // naturalizedBeforeBirth: true = Parent Nat BEFORE Child Birth (Child Born After)
                      // naturalizedBeforeBirth: false = Parent Nat AFTER Child Birth (Child Born Before)
                      // Checkbox: "Born BEFORE parent naturalized" -> implies Child Born Before -> naturalizedBeforeBirth = false
                      checked={parent.naturalizedBeforeBirth === false}
                      onChange={(e) => handleParentUpdate('naturalizedBeforeBirth', !e.target.checked)}
                      style={{ width: '16px', height: '16px', marginRight: '8px', accentColor: '#2563eb' }}
                    />
                    <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                      Born <strong>before</strong> parent naturalized
                      <Tooltip definition="If you were born before your parent became a Canadian citizen, different citizenship transmission rules apply — especially for pre-1947/1949 periods. Check this if the naturalization date shown above is after your birth date." />
                    </span>
                  </label>

                  {/* Under 21 Check - Only if Pre-1947/49 AND Born Before Nat */}
                  {getParentNatBucket() === 'PRE_1947' && parent.naturalizedBeforeBirth === false && (
                    <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#374151' }}>
                      <input
                        type="checkbox"
                        checked={parent.wasUnder21 ?? false}
                        onChange={(e) => handleParentUpdate('wasUnder21', e.target.checked)}
                        style={{ width: '16px', height: '16px', marginRight: '8px', accentColor: '#2563eb' }}
                      />
                      <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <strong>Under 21</strong> when parent naturalized
                        <Tooltip definition="Pre-1947 law allowed citizenship to pass to a child under 21 at the time their parent naturalized, even if the child was born before the naturalization. This was an exception to the general rule." />
                      </span>
                    </label>
                  )}
                </div>
              </div>
            </>
          )}

          <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: '16px' }}>
            {/* Birth Date Input - Only show for Applicant (Gen 0) in Simplified Mode, or everyone in Exact Mode */}
            {(useExactDates || generation === 0) && (
              <div style={{ ...inputGroupStyle, flex: 1 }}>
                <label style={labelStyle}>Birth Date</label>
                <div style={{ width: '100%' }}>
                  {useExactDates ? (
                    <Flatpickr
                      value={person.birthYear}
                      onChange={handleDateChange}
                      options={{
                        dateFormat: 'Y-m-d',
                        maxDate: getMaxDate(),
                        allowInput: true,
                        altInput: true,
                        altFormat: "F j, Y",
                      }}
                      style={{ ...dateInputStyle, backgroundColor: 'white' }}
                      placeholder="Select Date"
                    />
                  ) : (
                    <>
                      <select
                        value={getBirthBucket()}
                        onChange={(e) => handleSimplifiedDateChange('birth', e.target.value)}
                        style={selectStyle}
                      >
                        <option value="POST_2025">On or after December 15, 2025</option>
                        <option value="PRE_2025">Before December 15, 2025</option>
                      </select>
                      {getBirthBucket() === 'POST_2025' && (
                        <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#6b7280' }}>
                          December 15, 2025 is when <strong>Bill C-3</strong> came into effect, introducing new residency requirements for citizenship by descent.
                        </div>
                      )}
                    </>
                  )}

                  {isParentYounger && (
                    <div style={{ color: '#b91c1c', fontSize: '0.8rem', marginTop: '4px', fontWeight: '600' }}>
                      Error: Parent cannot be born after child.
                    </div>
                  )}
                </div>
              </div>
            )}

            <div style={{ ...inputGroupStyle, flex: 1 }}>
              <label style={labelStyle}>Canadian Citizen?</label>
              {isCalculated && !isNaturalizedButEligible ? (
                <div style={{
                  padding: '8px',
                  backgroundColor: '#f3f4f6',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  color: '#374151',
                  boxSizing: 'border-box',
                }}>
                  <div style={{ fontWeight: '600', marginBottom: '2px', wordBreak: 'break-word' }}>
                    {effectiveStatus === CITIZENSHIP_STATUS.NO ? 'Likely Not Eligible' : (effectiveStatus === 'NOT_SURE' ? 'More information is required' : 'Likely Citizen')}
                  </div>
                  <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                    {effectiveStatus === CITIZENSHIP_STATUS.NO
                      ? (getIneligibilityReason() || 'Based on ancestors')
                      : (isPre1947Exception ? 'Pre 1947/1949 Naturalized with Parent' : (sourceGen ? `Inherited via ${sourceGen}` : 'By Descent'))}
                  </div>
                </div>
              ) : generation === 0 ? (
                <div style={{
                  width: '100%',
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  boxSizing: 'border-box',
                  backgroundColor: person.calculatedStatus === null ? '#f0f9ff' : '#f3f4f6',
                  color: person.calculatedStatus === null ? '#0284c7' : '#6b7280',
                  fontWeight: person.calculatedStatus === null ? '600' : '400',
                  display: 'flex', alignItems: 'center', justifyContent: 'center'
                }}>
                  {person.calculatedStatus === null ? 'Calculating...' : getStatusLabel(person.isCitizen)}
                </div>
              ) : (
                <>
                  <select
                    name="isCitizen"
                    value={person.isCitizen}
                    onChange={handleInputChange}
                    style={selectStyle}
                  >
                    <option value={CITIZENSHIP_STATUS.NO}>{CITIZENSHIP_LABELS[CITIZENSHIP_STATUS.NO]}</option>
                    <option value="NOT_SURE">Not sure / Trying to Determine</option>
                    <option value={CITIZENSHIP_STATUS.BORN_IN_CANADA}>{CITIZENSHIP_LABELS[CITIZENSHIP_STATUS.BORN_IN_CANADA]}</option>
                    <option value={CITIZENSHIP_STATUS.BY_DESCENT}>{CITIZENSHIP_LABELS[CITIZENSHIP_STATUS.BY_DESCENT]}</option>
                    <option value={CITIZENSHIP_STATUS.NATURALIZED}>{CITIZENSHIP_LABELS[CITIZENSHIP_STATUS.NATURALIZED]}</option>
                  </select>
                  {person.isCitizen === 'NOT_SURE' && (
                    <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#6b7280' }}>
                      Not sure? Keep adding ancestors! Check the <strong>FAQ</strong> tab for guidance on researching your ancestor's citizenship status, or see <strong>Resources</strong> for genealogy tools.
                    </div>
                  )}
                  {person.isCitizen === CITIZENSHIP_STATUS.BY_DESCENT && (
                    <div style={{ marginTop: '6px', fontSize: '0.78rem', color: '#6b7280' }}>
                      <strong>By Descent</strong> means born outside Canada to a Canadian parent. Select this if the ancestor was born abroad and already a recognized Canadian citizen.
                    </div>
                  )}
                </>
              )}
              {parentMissingNatDate && (
                <div style={{ marginTop: '8px', fontSize: '0.8rem', color: '#374151', display: 'flex', alignItems: 'center', fontWeight: '500' }}>
                  Set Parent Naturalization Date <span style={{ marginLeft: '6px' }}>⚠️</span>
                </div>
              )}
              {person.isCitizen === CITIZENSHIP_STATUS.NATURALIZED && (!isCalculated || isNaturalizedButEligible) && (
                <div style={{ marginTop: '12px' }}>
                  <label style={labelStyle}>Naturalization Date</label>

                  {!useExactDates && child && (
                    <div style={{ marginBottom: '12px', padding: '8px 10px', backgroundColor: '#eef2ff', borderLeft: '3px solid #4f46e5', borderRadius: '4px', fontSize: '0.78rem', color: '#3730a3' }}>
                      <strong>Note:</strong> Because this person was Naturalized, their naturalization date relative to their child's birth affects eligibility under historical rules. Related questions will appear on the child's card.
                    </div>
                  )}

                  {/* Newfoundland Toggle for Simplified Mode */}
                  {!useExactDates && (
                    <>
                      <div style={{ marginBottom: '8px', padding: '8px 10px', backgroundColor: '#fffbeb', borderLeft: '3px solid #f59e0b', borderRadius: '4px', fontSize: '0.78rem', color: '#92400e' }}>
                        <strong>Why is this here?</strong> Newfoundland joined Canada on April 1, 1949 — a different cutoff date than the standard January 1, 1947. Only check this if your ancestor naturalized in Newfoundland or Labrador.
                      </div>
                      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#374151', marginBottom: '8px' }}>
                        <input
                          type="checkbox"
                          checked={person.naturalizedInNewfoundland || false}
                          onChange={(e) => onPersonChange(person.id, { ...person, naturalizedInNewfoundland: e.target.checked })}
                          style={{ width: '16px', height: '16px', marginRight: '8px', accentColor: '#ea580c' }}
                        />
                        <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          Naturalized in Newfoundland or Labrador?
                          <Tooltip definition="Newfoundland joined Canada on April 1, 1949, so ancestors who naturalized there before that date fall under a different cutoff than the standard January 1, 1947 rule that applies to the rest of Canada." />
                        </span>
                      </label>
                    </>
                  )}

                  <div style={{ width: '100%' }}>
                    {useExactDates ? (
                      <Flatpickr
                        value={person.citizenshipDate}
                        onChange={handleCitizenshipDateChange}
                        options={{
                          dateFormat: 'Y-m-d',
                          allowInput: true,
                          altInput: true,
                          altFormat: "F j, Y",
                        }}
                        style={{ ...dateInputStyle, backgroundColor: 'white' }}
                        placeholder="Select Date"
                      />
                    ) : (
                      <select
                        value={getNatBucket()}
                        onChange={(e) => handleSimplifiedDateChange('naturalization', e.target.value)}
                        style={selectStyle}
                      >
                        <option value="POST_1947">
                          After {person.naturalizedInNewfoundland ? 'April 1, 1949' : 'January 1, 1947'}
                        </option>
                        <option value="PRE_1947">
                          Before {person.naturalizedInNewfoundland ? 'April 1, 1949' : 'January 1, 1947'}
                        </option>
                      </select>
                    )}
                  </div>

                  {isNaturalizedButEligible && (
                    <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe', fontSize: '0.85rem', color: '#1e40af' }}>
                      <strong style={{ color: '#1f2937' }}>Note:</strong> <span style={{ color: '#1f2937' }}>Even though you Naturalized, you also appear to be a <strong>Citizen by Descent</strong> via your ancestors.</span>
                      <br /><br />
                      This means your citizenship likely started at birth, allowing you to pass it to children born <em>before</em> your naturalization date.
                    </div>
                  )}
                  {useExactDates && person.citizenshipDate &&
                    new Date(person.citizenshipDate) >= new Date(1947, 0, 1) &&
                    new Date(person.citizenshipDate) < new Date(1949, 3, 1) && (
                      <div style={{ marginTop: '8px', padding: '8px', backgroundColor: '#fff7ed', borderRadius: '6px', border: '1px solid #fed7aa' }}>
                        <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '0.9rem', color: '#9a3412' }}>
                          <input
                            type="checkbox"
                            checked={person.naturalizedInNewfoundland || false}
                            onChange={(e) => onPersonChange(person.id, { ...person, naturalizedInNewfoundland: e.target.checked })}
                            style={{ width: '16px', height: '16px', marginRight: '8px', accentColor: '#ea580c' }}
                          />
                          <span style={{ color: '#1f2937' }}>Naturalized in Newfoundland?</span>
                        </label>
                        <div style={{ fontSize: '0.8rem', color: '#374151', marginTop: '4px', marginLeft: '24px' }}>Special rules apply before April 1, 1949.</div>
                      </div>
                    )}
                </div>
              )}
            </div>
          </div>
          <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
            {onCalculate && child && (!isCalculated || isNaturalizedButEligible) && (!person.ancestors || person.ancestors.length === 0) && (
              <button onClick={onCalculate} style={{ ...buttonBaseStyle, backgroundColor: '#3b82f6', color: 'white' }}>
                Calculate
              </button>
            )}
            {(!isCalculated || isNaturalizedButEligible) && (!person.ancestors || person.ancestors.length === 0) && (
              <button onClick={() => {
                const isAlreadyCitizen = [
                  CITIZENSHIP_STATUS.BORN_IN_CANADA,
                  CITIZENSHIP_STATUS.BY_DESCENT,
                  CITIZENSHIP_STATUS.NATURALIZED
                ].includes(effectiveStatus);

                if (isAlreadyCitizen) {
                  onAddAncestor(person.id); // This now triggers the modal check in App.js
                  return;
                }
                onAddAncestor(person.id);
              }} style={{ ...buttonBaseStyle, backgroundColor: '#16a34a', color: 'white' }}>+ Add Parent</button>
            )}
            {child && (!isCalculated || isNaturalizedButEligible) && (!person.ancestors || person.ancestors.length === 0) && (
              <button onClick={() => onRemoveAncestor(person.id)} style={{ ...buttonBaseStyle, backgroundColor: '#ef4444', color: 'white' }}>Remove</button>
            )}
          </div>
        </div>

        {(showDocs) && (
          <div style={docsStyle}>
            <h4 style={{ marginTop: 0, marginBottom: '12px', color: '#111827' }}>Required Documentation</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', color: '#374151', fontSize: '0.9rem' }}>
              {(generation === 0 || person.alsoApplying) && (
                <>
                  <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.cit0014 || false} onChange={(e) => handleDocChange('cit0014', e.target.checked)} />
                    <span>Document Checklist (CIT 0014)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.cit0001 || false} onChange={(e) => handleDocChange('cit0001', e.target.checked)} />
                    <span>Application Form (CIT 0001)</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.personalID || false} onChange={(e) => handleDocChange('personalID', e.target.checked)} />
                    <span>Two (2) Pieces of Personal ID</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.photos || false} onChange={(e) => handleDocChange('photos', e.target.checked)} />
                    <span>Two (2) Citizenship Photos</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                    <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.fee || false} onChange={(e) => handleDocChange('fee', e.target.checked)} />
                    <span>Application Fee Receipt</span>
                  </label>
                </>
              )}

              <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '3px' }}
                  checked={person.documentation?.birthRecord || false}
                  onChange={(e) => handleDocChange('birthRecord', e.target.checked)}
                />
                <span>
                  {effectiveStatus === CITIZENSHIP_STATUS.BORN_IN_CANADA
                    ? (generation === 0 || person.alsoApplying ? 'Canadian Birth Certificate' : 'Birth / Baptism Record')
                    : (generation === 0 || person.alsoApplying ? 'Birth Certificate' : 'Birth / Baptism Record')}
                </span>
              </label>

              {generation > 0 && !person.alsoApplying && (
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: '3px' }}
                    checked={person.documentation?.secondaryRecords || false}
                    onChange={(e) => handleDocChange('secondaryRecords', e.target.checked)}
                  />
                  <span>Secondary Evidence (Census, Military, etc.)</span>
                </label>
              )}

              {generation > 0 && [CITIZENSHIP_STATUS.BY_DESCENT, CITIZENSHIP_STATUS.NATURALIZED].includes(person.isCitizen) && (
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: '3px' }}
                    checked={person.documentation?.citizenshipProof || false}
                    onChange={(e) => handleDocChange('citizenshipProof', e.target.checked)}
                  />
                  <span>Canadian Citizenship Certificate/Card</span>
                </label>
              )}

              <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  style={{ marginTop: '3px' }}
                  checked={person.documentation?.nameChange || false}
                  onChange={(e) => handleDocChange('nameChange', e.target.checked)}
                />
                <span>Name Change Records (if applicable)</span>
              </label>

              {generation === 0 && isDeepAncestry && (
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                  <input type="checkbox" style={{ marginTop: '3px' }} checked={person.documentation?.letterOfExplanation || false} onChange={(e) => handleDocChange('letterOfExplanation', e.target.checked)} />
                  <span>Letter of Explanation</span>
                </label>
              )}

              {showCheckbox && (
                <label style={{ display: 'flex', alignItems: 'start', gap: '8px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    style={{ marginTop: '3px' }}
                    checked={person.documentation?.residencyProof || false}
                    onChange={(e) => handleDocChange('residencyProof', e.target.checked)}
                  />
                  <span>Proof of parent 1,095 days physical presence in Canada prior to birth</span>
                </label>
              )}
            </div>
          </div>
        )}
      </div>
      <div style={treeLineStyle}>
        {person.ancestors && person.ancestors.map(ancestor => (
          <Person
            key={ancestor.id}
            person={ancestor}
            onPersonChange={onPersonChange}
            onAddAncestor={onAddAncestor}
            onRemoveAncestor={onRemoveAncestor}
            onCalculate={onCalculate}
            child={person}
            generation={generation + 1}
            showDocs={showDocs}
            isMobile={isMobile}
            useExactDates={useExactDates}
          />
        ))}
      </div>
    </div>
  );
};

export default Person;